import { ConversationId } from "@redotech/effect-api-shared/ids/definitions";
import type { ConversationListItemResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/conversation";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { useCallback, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useConversations } from "./use-conversation-data";

export interface UseConversationCardListResult {
  /** List of conversations */
  conversations: ConversationListItemResponse[];
  /** True when initial data is loading */
  isLoading: boolean;
  /** True when fetching more conversations */
  isFetchingMore: boolean;
  /** True if more pages available */
  hasNextPage: boolean;
  /** True during refresh operation */
  isRefreshing: boolean;
  /** Currently selected conversation ID */
  selectedConversationId: ConversationId | null;
  /** Select a conversation */
  selectConversation: (conversation: ConversationListItemResponse) => void;
  /** Refresh the list */
  refresh: () => Promise<void>;
  /** Load more conversations */
  loadMore: () => void;
  /** Get next conversation in list (for keyboard nav) */
  getNextConversationInList: () => ConversationListItemResponse | undefined;
  /** Get previous conversation in list (for keyboard nav) */
  getPreviousConversationInList: () => ConversationListItemResponse | undefined;
  /** Check if a conversation is the selected one */
  isConversationSelected: (
    conversation: ConversationListItemResponse,
  ) => boolean;
}

export function useConversationCardList(): UseConversationCardListResult {
  const navigate = useNavigate();
  const { conversationId: conversationIdString } = useParams<{
    conversationId: ConversationId;
  }>();
  const team = useRequiredContext(TeamContext);

  const selectedConversationId = conversationIdString
    ? ConversationId.make(conversationIdString)
    : null;

  const { data, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useConversations();
  const [isRefreshing, setIsRefreshing] = useState(false);

  const isLoading = data.length === 0 && isFetchingNextPage;

  const selectConversation = useHandler(
    (conversation: ConversationListItemResponse) => {
      navigate(`/stores/${team._id}/support-v2/${conversation.id}`);
    },
  );

  const refresh = useHandler(async () => {
    setIsRefreshing(true);
    try {
      fetchNextPage();
    } finally {
      setIsRefreshing(false);
    }
  });

  const loadMore = useHandler(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  });

  const selectedIndex = useMemo(() => {
    if (!selectedConversationId) {
      return -1;
    }
    return data.findIndex((conv) => conv.id === selectedConversationId);
  }, [data, selectedConversationId]);

  const getNextConversationInList = useHandler(() => {
    if (selectedIndex === -1 || selectedIndex >= data.length - 1) {
      return undefined;
    }
    return data[selectedIndex + 1];
  });

  const getPreviousConversationInList = useHandler(() => {
    if (selectedIndex <= 0) {
      return undefined;
    }
    return data[selectedIndex - 1];
  });

  // useCallback (not useHandler) so the function reference changes when selection
  // changes, triggering re-render in memoized ConversationCardList
  const isConversationSelected = useCallback(
    (conversation: ConversationListItemResponse) => {
      return conversation.id === selectedConversationId;
    },
    [selectedConversationId],
  );

  return {
    conversations: data,
    isLoading,
    isFetchingMore: isFetchingNextPage,
    hasNextPage,
    isRefreshing,
    selectedConversationId,
    selectConversation,
    refresh,
    loadMore,
    getNextConversationInList,
    getPreviousConversationInList,
    isConversationSelected,
  };
}
